// Date/time projection + option-list helpers shared by SingleForm,
// BatchForm, AIForm.
//
// IMPORTANT: this module mixes two PlainDate types whose `month` field
// has *different conventions*:
//
//   - `PlainDate` imported from `@viquard/doc-ui/components` (the type
//     the DatePicker / Calendar trafficks in) — `month` is **0-indexed**
//     (January = 0, May = 4). It mirrors `Date.getMonth()`.
//
//   - `parseISODate()` / `instantFromZonedWallClock()` from the
//     calendar's `dateUtils` — `month` is **1-indexed** (January = 1,
//     May = 5).
//
// Mixing them silently shifted every saved event by one calendar month
// (user picked May 7, the saved event landed on April 7). Inside this
// file we treat *every* PlainDate as the doc-ui 0-indexed shape (because
// that is what flows through DatePicker), and convert at the call
// boundary when handing values to / from the 1-indexed dateUtils helpers.

import type {
  PlainDate,
  SelectOption,
  TimePickerValue,
} from '@viquard/doc-ui/components';
import {
  formatMessage,
  type CalendarMessages,
} from '../../i18n.js';
import {
  instantFromZonedWallClock,
  isDateOnlyISO,
  localDateKey,
  localTime,
  parseISODate,
} from '../../utils/dateUtils.js';

const PAD2 = (n: number): string => (n < 10 ? `0${n}` : String(n));
const RECURRENCE_BASES: ReadonlySet<string> = new Set([
  'FREQ=DAILY',
  'FREQ=WEEKLY',
  'FREQ=WEEKLY;INTERVAL=2',
  'FREQ=MONTHLY',
]);

export const KIND_DEFAULT_REMINDER_VALUE = '__kind__';
export type DefaultReminderSelection =
  | number
  | null
  | typeof KIND_DEFAULT_REMINDER_VALUE;

/** Duration / recurrence option lists are built from `messages` rather
 *  than declared as module constants — the labels need to flip with
 *  the active locale and the value space is small enough that a fresh
 *  array per render is cheap. The values themselves are stable wire
 *  identifiers (`'30'`, `'FREQ=DAILY'`, …); only labels change. */
export function buildDurationOptions(
  messages: CalendarMessages,
): readonly SelectOption[] {
  return [
    { value: '30', label: formatMessage(messages.composerDurationMinutes, { count: 30 }) },
    { value: '45', label: formatMessage(messages.composerDurationMinutes, { count: 45 }) },
    { value: '60', label: messages.composerDurationOneHour },
    { value: '90', label: messages.composerDurationOneHalfHour },
    { value: '120', label: messages.composerDurationTwoHours },
    { value: '0', label: messages.composerDurationAllDay },
  ];
}

export function buildRecurOptions(
  messages: CalendarMessages,
): readonly SelectOption[] {
  return [
    { value: 'FREQ=DAILY', label: messages.composerRepeatDaily },
    { value: 'FREQ=WEEKLY', label: messages.composerRepeatWeekly },
    { value: 'FREQ=WEEKLY;INTERVAL=2', label: messages.composerRepeatBiweekly },
    { value: 'FREQ=MONTHLY', label: messages.composerRepeatMonthly },
  ];
}

export function recurrenceBaseFromRule(
  rule: string | null | undefined,
): string {
  if (rule === null || rule === undefined || rule.length === 0) return 'FREQ=WEEKLY';
  const base = rule
    .split(';')
    .filter((part) => !part.toUpperCase().startsWith('UNTIL='))
    .join(';');
  return RECURRENCE_BASES.has(base) ? base : 'FREQ=WEEKLY';
}

export function recurrenceUntilFromRule(
  rule: string | null | undefined,
  viewerTz: string | undefined,
): PlainDate | null {
  if (rule === null || rule === undefined) return null;
  const until = rule
    .split(';')
    .find((part) => part.toUpperCase().startsWith('UNTIL='))
    ?.slice('UNTIL='.length);
  if (until === undefined) return null;
  if (/^\d{8}$/.test(until)) {
    return {
      year: Number(until.slice(0, 4)),
      month: Number(until.slice(4, 6)) - 1,
      day: Number(until.slice(6, 8)),
    };
  }
  if (!/^\d{8}T\d{6}Z$/.test(until)) return null;
  return plainFromIso(
    `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}T${until.slice(9, 11)}:${until.slice(11, 13)}:${until.slice(13, 15)}.000Z`,
    viewerTz,
  );
}

export function buildRecurrenceRule(
  base: string,
  until: PlainDate | null,
  viewerTz: string | undefined,
): string {
  const safeBase = recurrenceBaseFromRule(base);
  if (until === null) return safeBase;
  const endOfDay = new Date(
    instantFromZonedWallClock(until.year, until.month + 1, until.day, 23, 59, viewerTz) +
      59_000,
  );
  const stamp =
    `${endOfDay.getUTCFullYear()}` +
    `${PAD2(endOfDay.getUTCMonth() + 1)}` +
    `${PAD2(endOfDay.getUTCDate())}T` +
    `${PAD2(endOfDay.getUTCHours())}` +
    `${PAD2(endOfDay.getUTCMinutes())}` +
    `${PAD2(endOfDay.getUTCSeconds())}Z`;
  return `${safeBase};UNTIL=${stamp}`;
}

export function comparePlainDate(a: PlainDate, b: PlainDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

export function buildReminderOptions(
  messages: CalendarMessages,
  options?: { readonly includeKindDefault?: boolean },
): readonly SelectOption[] {
  const items: SelectOption[] = [
    { value: 'none', label: messages.composerReminderNone },
    { value: '5', label: formatMessage(messages.composerReminderMinutes, { count: 5 }) },
    { value: '10', label: formatMessage(messages.composerReminderMinutes, { count: 10 }) },
    { value: '15', label: formatMessage(messages.composerReminderMinutes, { count: 15 }) },
    { value: '30', label: formatMessage(messages.composerReminderMinutes, { count: 30 }) },
    { value: '60', label: messages.composerReminderOneHour },
    { value: '1440', label: messages.composerReminderOneDay },
  ];
  if (options?.includeKindDefault === true) {
    return [
      { value: KIND_DEFAULT_REMINDER_VALUE, label: messages.composerReminderByKind },
      ...items,
    ];
  }
  return items;
}

export function titlePlaceholderForKind(
  kind: string,
  messages: CalendarMessages,
): string {
  return (
    messages.composerTitlePlaceholders[kind] ??
    messages.composerTitlePlaceholders.default ??
    'Event title'
  );
}

export function plusMinutes(iso: string, minutes: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Date(d.getTime() + minutes * 60_000).toISOString();
}

export function dateOnlyFromPlain(d: PlainDate): string {
  // Doc-ui PlainDate is 0-indexed → emit `MM` as `month + 1`.
  return `${d.year}-${PAD2(d.month + 1)}-${PAD2(d.day)}`;
}

/** Resolve a viewer-TZ wall clock (date + time) to an absolute UTC ISO
 *  string. Output is unambiguous regardless of where the consumer later
 *  parses it — `new Date(iso)` always reproduces the same instant. */
export function isoFromDateAndTime(
  d: PlainDate,
  t: TimePickerValue,
  viewerTz: string | undefined,
): string {
  // `instantFromZonedWallClock` expects 1-indexed month; doc-ui PlainDate
  // is 0-indexed. The +1 here is the boundary conversion.
  return new Date(
    instantFromZonedWallClock(d.year, d.month + 1, d.day, t.hour, t.minute, viewerTz),
  ).toISOString();
}

/** Pull `{year, month, day}` out of an ISO string in `viewerTz`. For
 *  date-only strings the date is returned verbatim (TZ-neutral); for
 *  full instants we project to viewerTz so a PT user editing an event
 *  with `startsAt = '...T07:30:00Z'` sees their local Tuesday, not UTC
 *  Tuesday. Returns a doc-ui-shaped (0-indexed month) PlainDate. */
export function plainFromIso(
  iso: string | null | undefined,
  viewerTz: string | undefined,
): PlainDate | null {
  if (iso === null || iso === undefined) return null;
  if (isDateOnlyISO(iso)) {
    const { year, month, day } = parseISODate(iso);
    return { year, month: month - 1, day };
  }
  const key = localDateKey(iso, viewerTz);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const { year, month, day } = parseISODate(key);
  return { year, month: month - 1, day };
}

export function timeFromIso(
  iso: string | null | undefined,
  viewerTz: string | undefined,
): TimePickerValue {
  if (iso === null || iso === undefined || isDateOnlyISO(iso)) {
    return { hour: 9, minute: 0 };
  }
  const t = localTime(iso, viewerTz);
  if (t === null) return { hour: 9, minute: 0 };
  const [hh, mm] = t.split(':').map(Number);
  return { hour: hh ?? 9, minute: mm ?? 0 };
}

export function durationFromIso(
  start: string | null,
  end: string | null,
  fallbackMinutes = 60,
): string {
  const fallback = String(fallbackMinutes);
  if (start === null || end === null) return fallback;
  if (isDateOnlyISO(start)) return '0';
  const a = new Date(start).getTime();
  const b = new Date(end).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return fallback;
  const minutes = Math.round((b - a) / 60_000);
  if ([30, 45, 60, 90, 120].includes(minutes)) return String(minutes);
  return fallback;
}
